import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Card,
    CardContent,
    Divider,
    Chip,
    Grid,
    Collapse,
    IconButton,
    Button,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress
} from '@mui/material';
import { getUserTransactions } from '../api/creditApi';
import { downloadInvoice, deleteTransaction } from '../api/invoiceApi';
import { updateUnpaidUser } from '../api/contractApi';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { formatCurrency } from '../utils/currency';

const Transactions = ({ user, affiliateId }) => {
    const [transactions, setTransactions] = useState([]);
    const [openRow, setOpenRow] = useState(null);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalCurrency, setTotalCurrency] = useState('EUR');
    const [downloadingInvoice, setDownloadingInvoice] = useState(null);
    const [downloadError, setDownloadError] = useState('');
    const [deletingTransaction, setDeletingTransaction] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [transactionToDelete, setTransactionToDelete] = useState(null);
    const [markingAsPaid, setMarkingAsPaid] = useState(null);
    const [paidConfirmOpen, setPaidConfirmOpen] = useState(false);
    const [transactionToPaid, setTransactionToPaid] = useState(null);

    // Check user role from localStorage
    const userRole = localStorage.getItem('role');
    const isOutgoingTransaction = (tx) => {
        if (tx?.decrease === true) return true;
        const type = String(tx?.type || '').toLowerCase();
        if (tx?.source === 'shop') return true;
        return type.startsWith('shop_');
    };
    const formatAmount = (value) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return '0.00';
        return numericValue.toFixed(2);
    };
    const getTransactionCurrency = (tx) => tx?.currency || 'EUR';
    const formatTransactionAmount = (value, tx) => formatCurrency(value, getTransactionCurrency(tx));
    const shouldUsePreopenedDownloadTab = () => {
        if (typeof navigator === 'undefined') return false;
        const userAgent = navigator.userAgent || '';
        return /Android/i.test(userAgent) && /Firefox/i.test(userAgent);
    };

    const fetchTransactions = useCallback(async () => {
        try {
            const data = await getUserTransactions(affiliateId, user.id);
            setTransactions(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setTransactions([]);
        }
    }, [affiliateId, user.id]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    useEffect(() => {
        // Filtreerime tehingud, kus decrease = true ja liidame nende summad
        const outgoingTransactions = transactions.filter((tx) => isOutgoingTransaction(tx));
        const currencies = [...new Set(outgoingTransactions.map((tx) => getTransactionCurrency(tx)))];
        const sum = outgoingTransactions
            .reduce((acc, tx) => acc + parseFloat(tx.amount), 0);

        setTotalAmount(sum);
        setTotalCurrency(currencies.length === 1 ? currencies[0] : null);
    }, [transactions]);

    const handleRowClick = (id) => {
        if (openRow === id) {
            setOpenRow(null);
        } else {
            setOpenRow(id);
        }
    };

    const handleDownloadInvoice = async (transactionId) => {
        setDownloadingInvoice(transactionId);
        setDownloadError('');
        let preopenedTab = null;

        try {
            // Firefox on Android may block async-triggered downloads unless
            // we keep the action tied to the original user click.
            if (shouldUsePreopenedDownloadTab()) {
                preopenedTab = window.open('', '_blank', 'noopener');
            }
            await downloadInvoice(transactionId, { preopenedTab });
        } catch (error) {
            if (preopenedTab && !preopenedTab.closed) {
                preopenedTab.close();
            }
            console.error('Error downloading invoice:', error);
            setDownloadError('Failed to download invoice. Please try again.');
        } finally {
            setDownloadingInvoice(null);
        }
    };

    const handleMakePayment = (paymentUrl) => {
        if (paymentUrl) {
            window.open(paymentUrl, '_blank');
        }
    };

    const handleDeleteTransaction = async (transactionId) => {
        setDeletingTransaction(transactionId);
        setDeleteError('');
        setDeleteConfirmOpen(false);

        try {
            await deleteTransaction(transactionId);
            // Remove the deleted transaction from the state
            setTransactions(prev => prev.filter(tx => tx.id !== transactionId));
            setOpenRow(null); // Close the expanded row
        } catch (error) {
            console.error('Error deleting transaction:', error);
            setDeleteError('Failed to delete transaction. Please try again.');
        } finally {
            setDeletingTransaction(null);
        }
    };

    const handleDeleteClick = (transactionId) => {
        setTransactionToDelete(transactionId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setTransactionToDelete(null);
    };

    const handlePaidClick = (transaction) => {
        setTransactionToPaid(transaction);
        setPaidConfirmOpen(true);
    };

    const handlePaidCancel = () => {
        setPaidConfirmOpen(false);
        setTransactionToPaid(null);
    };

    const handleMarkAsPaid = async () => {
        if (!transactionToPaid) return;
        
        setMarkingAsPaid(transactionToPaid.id);
        try {
            await updateUnpaidUser(transactionToPaid.id);
            setPaidConfirmOpen(false);
            setTransactionToPaid(null);
            // Refresh transactions after marking as paid
            await fetchTransactions();
        } catch (error) {
            console.error('Error marking transaction as paid:', error);
        } finally {
            setMarkingAsPaid(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('et-EE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const renderPaymentButton = (tx) => {
        if (userRole === 'trainer') return null;
        const hasPersistedTransactionId = typeof tx?.id === 'number';

        // Don't show any button if decrease is false (credit transactions)
        if (!tx.decrease) return null;

        const isDownloading = downloadingInvoice === tx.id;
        const isPending = tx.status === 'pending';
        const isSuccess = tx.status === 'success' || tx.status === 'completed';
        const hasPaymentUrl = tx.paymentMetadata && tx.paymentMetadata.paymentUrl;

        // Show Make Payment button if status is pending and paymentUrl exists
        if (isPending && hasPaymentUrl) {
            return (
                <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<PaymentIcon />}
                    onClick={() => handleMakePayment(tx.paymentMetadata.paymentUrl)}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { xs: '100%', sm: '200px' },
                        minWidth: { sm: '150px' },
                        borderRadius: 20,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    Make Payment
                </Button>
            );
        }

        // Show Download Invoice button if status is success/completed
        if (isSuccess) {
            if (!hasPersistedTransactionId) return null;
            return (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={isDownloading ? <DownloadIcon /> : <PictureAsPdfIcon />}
                    onClick={() => handleDownloadInvoice(tx.id)}
                    disabled={isDownloading}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { xs: '100%', sm: '200px' },
                        minWidth: { sm: '150px' },
                        borderRadius: 20,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&:hover': {
                            transform: !isDownloading ? 'translateY(-2px)' : 'none',
                            boxShadow: !isDownloading ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    {isDownloading ? 'Downloading...' : 'Download PDF'}
                </Button>
            );
        }

        // Don't show any button for pending without paymentUrl or other statuses
        return null;
    };

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', px: { xs: 0, sm: 2 } }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                mb: 3
            }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: { xs: 0, sm: 0 } }}>
                    Transactions
                </Typography>
                <Paper
                    elevation={3}
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: 2,
                        background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)',
                        alignSelf: { xs: 'stretch', sm: 'auto' }
                    }}
                >
                    <Typography variant="subtitle2" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                        <AccountBalanceWalletIcon sx={{ mr: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                        <strong>Total expenses:</strong>&nbsp;{totalCurrency ? formatCurrency(totalAmount, totalCurrency) : 'Multiple currencies'}
                    </Typography>
                </Paper>
            </Box>

            {downloadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {downloadError}
                </Alert>
            )}

            {deleteError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {deleteError}
                </Alert>
            )}

            <Paper
                elevation={3}
                sx={{
                    overflow: 'hidden',
                    borderRadius: 2,
                    background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)'
                }}
            >
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: { xs: 300, sm: 600 } }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 12px', sm: '16px' },
                                    minWidth: { xs: '80px', sm: 'auto' }
                                }}>
                                    Date
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 12px', sm: '16px' },
                                    minWidth: { xs: '100px', sm: 'auto' }
                                }}>
                                    Description
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 12px', sm: '16px' },
                                    minWidth: { xs: '60px', sm: 'auto' }
                                }}>
                                    Amount
                                </TableCell>

                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map((tx) => {
                                const sign = isOutgoingTransaction(tx) ? '-' : '+';
                                const displayAmount = `${sign}${formatTransactionAmount(tx.amount, tx)}`;
                                const isOpen = openRow === tx.id;
                                const isDeleting = deletingTransaction === tx.id;

                                return (
                                    <React.Fragment key={tx.id}>
                                        {/* Main Row */}
                                        <TableRow
                                            hover
                                            onClick={() => handleRowClick(tx.id)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.04)'
                                                }
                                            }}
                                        >
                                            <TableCell sx={{
                                                padding: { xs: '8px 12px', sm: '16px' },
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                {formatDate(tx.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{
                                                padding: { xs: '8px 12px', sm: '16px' },
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: { xs: '100px', sm: '200px' }
                                                    }}
                                                >
                                                    {tx.description}
                                                    {tx.isFamilyMember && tx.familyMember?.fullName && (
                                                        <span style={{ color: '#666', fontStyle: 'italic' }}>
                                                            {' '}({tx.familyMember.fullName})
                                                        </span>
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                color: isOutgoingTransaction(tx) ? '#e74c3c' : '#2ecc71',
                                                fontWeight: 'bold',
                                                padding: { xs: '8px 12px', sm: '16px' },
                                                fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                            }}>
                                                {displayAmount}
                                            </TableCell>

                                        </TableRow>

                                        {/* Detail Row */}
                                        <TableRow>
                                            <TableCell
                                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                                colSpan={4}
                                            >
                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: { xs: 1, sm: 2 } }}>
                                                        {/* Action Buttons */}
                                                        <Box sx={{
                                                            mb: 3,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            gap: { xs: 1, sm: 2 },
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            alignItems: 'center'
                                                        }}>
                                                            {/* Payment/Invoice Button - Only show for regular users */}
                                                            {renderPaymentButton(tx)}

                                                            {/* PAID Button - Only show for affiliate users and when status is not success/completed */}
                                                            {userRole === 'affiliate' && tx.source !== 'shop' && tx.status !== 'success' && tx.status !== 'completed' && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="success"
                                                                    size="small"
                                                                    startIcon={markingAsPaid === tx.id ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                                                                    onClick={() => handlePaidClick(tx)}
                                                                    disabled={markingAsPaid === tx.id}
                                                                    sx={{
                                                                        width: { xs: '100%', sm: 'auto' },
                                                                        maxWidth: { xs: '100%', sm: '150px' },
                                                                        minWidth: { sm: '120px' },
                                                                        borderRadius: 20,
                                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                                        '&:hover': {
                                                                            transform: markingAsPaid !== tx.id ? 'translateY(-2px)' : 'none',
                                                                            boxShadow: markingAsPaid !== tx.id ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                                                                        },
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    {markingAsPaid === tx.id ? 'Processing...' : 'PAID'}
                                                                </Button>
                                                            )}

                                                            {/* Delete Button - Only show for affiliate users */}
                                                            {userRole === 'affiliate' && tx.source !== 'shop' && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="error"
                                                                    size="small"
                                                                    startIcon={<DeleteIcon />}
                                                                    onClick={() => handleDeleteClick(tx.id)}
                                                                    disabled={isDeleting}
                                                                    sx={{
                                                                        width: { xs: '100%', sm: 'auto' },
                                                                        maxWidth: { xs: '100%', sm: '150px' },
                                                                        minWidth: { sm: '120px' },
                                                                        borderRadius: 20,
                                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                                        '&:hover': {
                                                                            transform: !isDeleting ? 'translateY(-2px)' : 'none',
                                                                            boxShadow: !isDeleting ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                                                                        },
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                                                </Button>
                                                            )}
                                                        </Box>

                                                        <Typography
                                                            variant="h6"
                                                            gutterBottom
                                                            component="div"
                                                            sx={{
                                                                fontWeight: 'medium',
                                                                mb: 3,
                                                                fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                                            }}
                                                        >
                                                            Transaction Details
                                                        </Typography>

                                                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                                                            {/* Transaction Overview Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card
                                                                    elevation={2}
                                                                    sx={{
                                                                        height: '100%',
                                                                        borderRadius: 2,
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                                        },
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                                        <Typography
                                                                            variant="h6"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                color: '#2c3e50',
                                                                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                                                                fontWeight: 'medium'
                                                                            }}
                                                                        >
                                                                            <ReceiptIcon sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                                                                            Invoice Information
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        mr: { sm: 1 },
                                                                                        minWidth: { sm: 140 },
                                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                    }}
                                                                                >
                                                                                    Affiliate Name:
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                                >
                                                                                    {tx.affiliate.name}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        mr: { sm: 1 },
                                                                                        minWidth: { sm: 140 },
                                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                    }}
                                                                                >
                                                                                    Invoice Number:
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                                >
                                                                                    {tx.invoiceNumber || 'N/A'}
                                                                                </Typography>
                                                                            </Box>

                                                                            {tx.isFamilyMember && tx.familyMember?.fullName && (
                                                                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            fontWeight: 'bold',
                                                                                            mr: { sm: 1 },
                                                                                            minWidth: { sm: 140 },
                                                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                        }}
                                                                                    >
                                                                                        Family Member:
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                                    >
                                                                                        {tx.familyMember.fullName}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 0 } }}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        mr: { sm: 1 },
                                                                                        minWidth: { sm: 140 },
                                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                    }}
                                                                                >
                                                                                    Status:
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={tx.status || "N/A"}
                                                                                    color={
                                                                                        tx.status === 'completed' || tx.status === 'success' ? 'success' :
                                                                                            tx.status === 'pending' ? 'warning' :
                                                                                                tx.status === 'failed' ? 'error' :
                                                                                                    'default'
                                                                                    }
                                                                                    size="small"
                                                                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Payment Information Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card
                                                                    elevation={2}
                                                                    sx={{
                                                                        height: '100%',
                                                                        borderRadius: 2,
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                                        },
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                                        <Typography
                                                                            variant="h6"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                color: '#2c3e50',
                                                                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                                                                fontWeight: 'medium'
                                                                            }}
                                                                        >
                                                                            <PaymentIcon sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                                                                            Payment Information
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        mr: { sm: 1 },
                                                                                        minWidth: { sm: 140 },
                                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                    }}
                                                                                >
                                                                                    Total Amount:
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        color: tx.decrease ? '#e74c3c' : '#2ecc71',
                                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                    }}
                                                                                >
                                                                                    {formatTransactionAmount(tx.amount, tx)}
                                                                                </Typography>
                                                                            </Box>

                                                                            {tx.creditAmount > 0 && (
                                                                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            fontWeight: 'bold',
                                                                                            mr: { sm: 1 },
                                                                                            minWidth: { sm: 140 },
                                                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                        }}
                                                                                    >
                                                                                        Credit applied:
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                                    >
                                                                                        {formatTransactionAmount(tx.creditAmount, tx)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 'bold',
                                                                                        mr: { sm: 1 },
                                                                                        minWidth: { sm: 140 },
                                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                                    }}
                                                                                >
                                                                                    Created At:
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                                >
                                                                                    {formatDate(tx.createdAt)}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Description Card */}
                                                            <Grid item xs={12}>
                                                                <Card
                                                                    elevation={2}
                                                                    sx={{
                                                                        borderRadius: 2,
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                                        },
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                                        <Typography
                                                                            variant="h6"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                color: '#2c3e50',
                                                                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                                                                fontWeight: 'medium'
                                                                            }}
                                                                        >
                                                                            <DescriptionIcon sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                                                                            Description
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ p: { xs: 1, sm: 1 } }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                            >
                                                                                {tx.description || "No description available"}
                                                                            </Typography>
                                                                            {Array.isArray(tx.purchasedItems) && tx.purchasedItems.length > 0 && (
                                                                                <Box sx={{ mt: 1.5 }}>
                                                                                    {tx.purchasedItems.map((item, index) => (
                                                                                        <Typography
                                                                                            key={`${tx.id}-item-${index}`}
                                                                                            variant="body2"
                                                                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                                                        >
                                                                                            {item.productName} x {item.quantity}
                                                                                        </Typography>
                                                                                    ))}
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {/* Paid Confirmation Dialog */}
            <Dialog
                open={paidConfirmOpen}
                onClose={handlePaidCancel}
                aria-labelledby="paid-dialog-title"
                aria-describedDescription="paid-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="paid-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        color: 'success.main'
                    }}
                >
                    Confirm Payment
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="paid-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' }, mb: 2 }}
                    >
                        Are you sure you want to mark this transaction as paid?
                    </DialogContentText>
                    {transactionToPaid && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Description:</strong> {transactionToPaid.description}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mt: 1 }}>
                                <strong>Amount:</strong> {formatTransactionAmount(transactionToPaid.amount, transactionToPaid)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mt: 1 }}>
                                <strong>User:</strong> {user?.fullName || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handlePaidCancel}
                        disabled={markingAsPaid !== null}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMarkAsPaid}
                        color="success"
                        variant="contained"
                        disabled={markingAsPaid !== null}
                        startIcon={markingAsPaid !== null ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: markingAsPaid === null ? 'translateY(-1px)' : 'none',
                                boxShadow: markingAsPaid === null ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        {markingAsPaid !== null ? 'Processing...' : 'Mark as Paid'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedDescription="delete-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="delete-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="delete-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        Are you sure you want to delete this transaction? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleDeleteTransaction(transactionToDelete)}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Transactions;